import { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFormMetadata } from '../store/formEditorSlice';
import { RichTextEditor } from './RichTextEditor.jsx';
import { AlertCircle } from 'lucide-react';

const PICSEditor = memo(() => {
  const dispatch = useDispatch();
  const formMetadata = useSelector(state => state.formEditor.formMetadata);
  const { pics, supported_languages } = formMetadata;

  const handlePICSChange = (changes) => {
    const updates = {
      pics: {
        ...formMetadata.pics,
        ...changes
      }
    };
    dispatch(updateFormMetadata({ updates }));
  };

  const handleStyleChange = (field, value) => {
    const updates = {
      pics: {
        ...formMetadata.pics,
        style: {
          ...formMetadata.pics.style,
          [field]: value
        }
      }
    };
    dispatch(updateFormMetadata({ updates }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="pics-enabled"
          checked={pics.enabled}
          onChange={(e) => handlePICSChange({ enabled: e.target.checked })}
          className="h-4 w-4 text-blue-600 rounded border-gray-300"
        />
        <label htmlFor="pics-enabled" className="text-sm font-medium text-gray-700">
          Enable Personal Information Collection Statement
        </label>
      </div>

      {pics.enabled && (
        <>
          {/* Simplified Warning Message */}
          <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-700">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
            <p className="text-sm">
              Text indentation may appear differently in the final form. Please check the preview.
            </p>
          </div>

          <div className="space-y-4">
            {supported_languages.map(lang => (
              <div key={lang} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  PICS Content ({lang.toUpperCase()})
                </label>
                <RichTextEditor
                  value={pics.content[lang] || ''}
                  onChange={(newContentText) => {
                    const newContent = {
                      ...pics.content,
                      [lang]: newContentText
                    };
                    handlePICSChange({ content: newContent });
                  }
                }
                fieldName="pics"
                language={lang}
                collapsible={true}
                defaultCollapsed={true}
                title={`Personal Information Collection Statement (${lang})`}
                />
              </div>
            ))}
          </div>

          <div className="space-y-4 pt-4 border-t">
            <h4 className="text-sm font-medium text-gray-700">Appearance Settings</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600">
                  Desktop Max Height
                </label>
                <input
                  type="text"
                  value={pics.style.maxHeight}
                  onChange={(e) => handleStyleChange('maxHeight', e.target.value)}
                  placeholder="e.g., 200px"
                  className="mt-1 w-full border rounded-md p-2"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600">
                  Mobile Max Height
                </label>
                <input
                  type="text"
                  value={pics.style.mobileMaxHeight}
                  onChange={(e) => handleStyleChange('mobileMaxHeight', e.target.value)}
                  placeholder="e.g., 150px"
                  className="mt-1 w-full border rounded-md p-2"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

export default PICSEditor;